import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import ButtonAndLink from 'App/components/ButtonAndLink/ButtonAndLink'
import HTMLMapper from 'App/Epi/HTMLMapper'
import {
  BodyWrapper,
  HeadingWrapper,
  ImageWrapper,
  PhotoTextBlockWrapper,
  TextWrapper,
} from './PhotoTextBlock.styles'

import {
  createImageAltTextFromFileName,
  getFullImageUrl,
  getStandardSrcset,
  LazyLoadWrapper,
} from '@trr/internal-helpers'

interface PhotoSectionProps {
  image?: string
  useLargeImage?: boolean
}
interface BaseContentProps {
  heading: string
  body?: string
  customCategory?: string
  link?: {
    text: string
    url: string
  }
  button?: {
    text: string
    url: string
  }
  useColor?: boolean
  imagePosition?: 'left' | 'right'
  image?: string
  useLeftAlignedHeadingIfImageNotProvided?: boolean
}

export interface PhotoTextBlockProps {
  content: BaseContentProps & {
    color?: string
    useLargeImage?: boolean
  }
}

const TextSection = ({
  heading,
  body,
  customCategory,
  link,
  button,
  useColor,
  imagePosition,
  image,
  useLeftAlignedHeadingIfImageNotProvided = false,
}: BaseContentProps) => {
  return (
    <TextWrapper
      data-testid="text-section"
      useColor={Boolean(useColor)}
      imagePosition={imagePosition ?? 'right'}
      hasImage={Boolean(image)}
      useLeftAlignedHeadingIfImageNotProvided={
        useLeftAlignedHeadingIfImageNotProvided
      }
    >
      {customCategory && (
        <Typography
          variant="subtitle2"
          component="p"
          color="text.secondary"
          mx={{ md: 8 }}
          mb={1}
        >
          {customCategory}
        </Typography>
      )}
      {heading && (
        <HeadingWrapper
          hasImage={!!image}
          useLeftAlignedHeadingIfImageNotProvided={
            useLeftAlignedHeadingIfImageNotProvided
          }
        >
          <Typography variant="h2">{heading}</Typography>
        </HeadingWrapper>
      )}
      <BodyWrapper
        hasImage={!!image}
        useLeftAlignedHeadingIfImageNotProvided={
          useLeftAlignedHeadingIfImageNotProvided
        }
      >
        {body && <HTMLMapper body={body} />}
        {((button?.text && button?.url) || (link?.text && link?.url)) && (
          <Box mt={5}>
            <ButtonAndLink button={button} link={link} isSecondaryButton />
          </Box>
        )}
      </BodyWrapper>
    </TextWrapper>
  )
}

export const PhotoSection = ({
  image,
  useLargeImage = false,
}: PhotoSectionProps) => {
  const { MEDIA_URL } = window.APP_CONFIGURATION.COMMON

  return (
    <>
      {image && (
        <ImageWrapper useLargeImage={useLargeImage}>
          <LazyLoadWrapper className="LazyLoadWrapper">
            <img
              src={getFullImageUrl(MEDIA_URL, image, 768)}
              srcSet={getStandardSrcset(MEDIA_URL, image, [600])}
              sizes="(max-width: 600px) 100vw, 600px"
              alt={createImageAltTextFromFileName(image)}
              data-testid="photo-section"
            />
          </LazyLoadWrapper>
        </ImageWrapper>
      )}
    </>
  )
}

const PhotoTextBlock = ({
  content: {
    heading,
    body,
    customCategory,
    link,
    button,
    image,
    useLargeImage = false,
    imagePosition = 'right',
    color = 'yellow',
    useColor = true,
    useLeftAlignedHeadingIfImageNotProvided = false,
  },
}: PhotoTextBlockProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const components = {
    photo: <PhotoSection image={image} useLargeImage={useLargeImage} />,
    text: (
      <TextSection
        heading={heading}
        body={body}
        customCategory={customCategory}
        link={link}
        button={button}
        useColor={useColor}
        imagePosition={imagePosition}
        image={image}
        useLeftAlignedHeadingIfImageNotProvided={
          useLeftAlignedHeadingIfImageNotProvided
        }
      />
    ),
  }
  const renderPhotoTextContent = () => {
    if (isMobile) {
      return (
        <>
          {components.photo}
          {components.text}
        </>
      )
    }

    const showPhotoFirst = imagePosition === 'left'
    return (
      <>
        {showPhotoFirst ? components.photo : components.text}
        {showPhotoFirst ? components.text : components.photo}
      </>
    )
  }
  return (
    <Box component="section">
      <PhotoTextBlockWrapper
        bgColor={color}
        useColor={useColor}
        hasImage={!!image}
        useLeftAlignedHeadingIfImageNotProvided={
          useLeftAlignedHeadingIfImageNotProvided
        }
      >
        {renderPhotoTextContent()}
      </PhotoTextBlockWrapper>
    </Box>
  )
}

export default PhotoTextBlock
